import type { ReactElement, SVGProps } from 'react';

/**
 * Custom icon set (ART_BIBLE §9): 24×24 grid, 2 px stroke, round caps and joins, flat
 * fills with one neon accent. Icons are decorative by default; pass `title` to label one.
 */

const PATHS = {
  play: <path d="M8 5.5v13l10.5-6.5z" fill="currentColor" />,
  online: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M3 12h18M12 3c3.2 3 3.2 15 0 18M12 3c-3.2 3-3.2 15 0 18" />
    </>
  ),
  profile: (
    <>
      <path d="M4 12.5h16M7.2 12.5c0-4.6 1.8-7.5 4.8-7.5s4.8 2.9 4.8 7.5" />
      <path d="M6 21c0-3 2.6-5 6-5s6 2 6 5" />
    </>
  ),
  collection: (
    <>
      <path d="M12 8V6.5a2 2 0 1 0-2-2" />
      <path d="M12 8l8 5.2a1.5 1.5 0 0 1-.8 2.8H4.8A1.5 1.5 0 0 1 4 13.2z" />
    </>
  ),
  trophy: (
    <>
      <path d="M8 4h8v5a4 4 0 0 1-8 0zM8 6H5.5A2.5 2.5 0 0 0 8 10M16 6h2.5A2.5 2.5 0 0 1 16 10" />
      <path d="M12 13v4M8.5 20h7M9.5 17h5" />
    </>
  ),
  settings: (
    <>
      <path d="M4 7h9M19 7h1M4 17h1M11 17h9" />
      <circle cx="16" cy="7" r="2.2" />
      <circle cx="8" cy="17" r="2.2" />
    </>
  ),
  news: (
    <>
      <path d="M5 4h12v15a1 1 0 0 1-1 1H6a2 2 0 0 1-2-2V5a1 1 0 0 1 1-1z" />
      <path d="M17 8h2.5a.5.5 0 0 1 .5.5V18a2 2 0 0 1-2 2M8 8.5h6M8 12h6M8 15.5h3.5" />
    </>
  ),
  back: <path d="M14.5 5.5L8 12l6.5 6.5" />,
  close: <path d="M6 6l12 12M18 6L6 18" />,
  check: <path d="M5 12.5l4.5 4.5L19 7.5" />,
  lock: (
    <>
      <rect x="6" y="11" width="12" height="9" rx="2" />
      <path d="M8.5 11V8a3.5 3.5 0 0 1 7 0v3" />
    </>
  ),
  volume: (
    <>
      <path d="M4 10v4h3.5L12 17.5v-11L7.5 10z" />
      <path d="M15.5 9a4.2 4.2 0 0 1 0 6M18 6.8a7.6 7.6 0 0 1 0 10.4" />
    </>
  ),
  spark: <path d="M12 3l1.9 5.1L19 10l-5.1 1.9L12 17l-1.9-5.1L5 10l5.1-1.9zM18.5 16.5v3M17 18h3" />,
  bolt: <path d="M13 3L5 14h6l-1 7 8-11h-6z" />,
  star: <path d="M12 3.5l2.6 5.4 5.9.8-4.3 4.1 1 5.9-5.2-2.8-5.3 2.8 1-5.9L3.5 9.7l5.9-.8z" />,
  key: (
    <>
      <circle cx="8" cy="12" r="4" />
      <path d="M12 12h9M18 12v3M15.5 12v2" />
    </>
  ),
  target: (
    <>
      <circle cx="12" cy="12" r="8.5" />
      <circle cx="12" cy="12" r="4.5" />
      <circle cx="12" cy="12" r="1" fill="currentColor" />
    </>
  ),
  calendar: (
    <>
      <rect x="4" y="5.5" width="16" height="14.5" rx="2" />
      <path d="M4 10h16M8.5 3.5v4M15.5 3.5v4" />
    </>
  ),
  book: (
    <>
      <path d="M12 6.5C10 5 7 4.5 4.5 5v13c2.5-.5 5.5 0 7.5 1.5 2-1.5 5-2 7.5-1.5V5C17 4.5 14 5 12 6.5z" />
      <path d="M12 6.5v13" />
    </>
  ),
  keyboard: (
    <>
      <rect x="3" y="6.5" width="18" height="11" rx="2" />
      <path d="M7 10.5h.01M10 10.5h.01M13 10.5h.01M16 10.5h.01M8 14h8" />
    </>
  ),
  palette: (
    <>
      <path d="M12 4a8 8 0 1 0 0 16c1.4 0 2-1 1.4-2.2-.6-1.2.2-2.3 1.5-2.3H17a3 3 0 0 0 3-3A8 8 0 0 0 12 4z" />
      <path d="M8 11h.01M11 8h.01M15 9h.01" />
    </>
  ),
  eye: (
    <>
      <path d="M2.5 12S6 5.5 12 5.5 21.5 12 21.5 12 18 18.5 12 18.5 2.5 12 2.5 12z" />
      <circle cx="12" cy="12" r="2.8" />
    </>
  ),
  controller: (
    <>
      <path d="M7 8h10a4 4 0 0 1 4 4.2l-.4 3.2a2.2 2.2 0 0 1-4 .9L15 14h-6l-1.6 2.3a2.2 2.2 0 0 1-4-.9L3 12.2A4 4 0 0 1 7 8z" />
      <path d="M8 10.5v2M7 11.5h2M16 11h.01M18 12.5h.01" />
    </>
  ),
  globe: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M3 12h18M12 3c3.2 3 3.2 15 0 18M12 3c-3.2 3-3.2 15 0 18" />
    </>
  ),
  bell: (
    <>
      <path d="M6 17V11a6 6 0 0 1 12 0v6l1.5 2h-15zM10 21h4" />
    </>
  ),
  shield: <path d="M12 3l7.5 3v5.5c0 4.6-3 8-7.5 9.5-4.5-1.5-7.5-4.9-7.5-9.5V6z" />,
  pause: <path d="M8 5v14M16 5v14" />,
  chat: <path d="M4 5.5h16v10H12l-4.5 3.5v-3.5H4z" />,
  pin: <path d="M12 21v-7M8 4h8l-1 5 2.5 3.5H6.5L9 9z" />,
  clock: (
    <>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M12 7v5l3.5 2" />
    </>
  ),
  map: <path d="M3.5 6.5l5.5-2 6 2 5.5-2v13l-5.5 2-6-2-5.5 2zM9 4.5v13M15 6.5v13" />,
  alert: <path d="M12 4l9 15.5H3zM12 10v4.5M12 17.5v.5" />,
  arrowUp: <path d="M12 19V5M6 11l6-6 6 6" />,
  arrowDown: <path d="M12 5v14M6 13l6 6 6-6" />,
  arrowLeft: <path d="M19 12H5M11 6l-6 6 6 6" />,
  arrowRight: <path d="M5 12h14M13 6l6 6-6 6" />,
  rotate: <path d="M19 12a7 7 0 1 1-2.2-5.1M19 4.5v4h-4" />,
} satisfies Record<string, ReactElement>;

export type IconName = keyof typeof PATHS;

export interface IconProps extends Omit<SVGProps<SVGSVGElement>, 'children'> {
  name: IconName;
  size?: number | string;
  /** Accessible label; without it the icon is hidden from screen readers. */
  title?: string;
}

export function Icon({ name, size = '1.5em', title, ...rest }: IconProps) {
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      role={title ? 'img' : undefined}
      aria-hidden={title ? undefined : true}
      aria-label={title}
      focusable="false"
      {...rest}
    >
      {PATHS[name]}
    </svg>
  );
}
